import { useEffect, useRef } from 'react'
// react-router 6.11 exports this as unstable_useBlocker (stabilized later).
import { unstable_useBlocker as useBlocker } from 'react-router-dom-v6'
import { useTranslation } from 'react-i18next'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@clouway/ui'

// Confirms before leaving while `when` is true: intercepts in-SPA navigation
// via react-router's useBlocker (data router) with a styled AlertDialog, and
// full-page unload via beforeunload. Render inside a form whose dirty state
// drives `when`.
export function UnsavedChangesGuard({ when }: { when: boolean }) {
  const { t } = useTranslation('common')
  const blocker = useBlocker(when)
  const open = blocker.state === 'blocked'
  // Set when the user confirms leaving, so the close that AlertDialogAction
  // triggers does not also fire reset(). For a POP navigation (back button)
  // proceed() defers to a popstate; a reset() racing in behind it deletes the
  // blocker first and the navigation never happens — only the PUSH path
  // (sidebar links) survived the race. Suppressing the reset fixes both.
  const proceeding = useRef(false)

  useEffect(() => {
    if (!when) return
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [when])

  return (
    <AlertDialog
      open={open}
      // Only dismissals that are NOT a confirmed leave (Esc / overlay / Cancel)
      // should reset the blocker and keep the user on the page.
      onOpenChange={(next) => {
        if (next) return
        if (proceeding.current) {
          proceeding.current = false
          return
        }
        blocker.reset?.()
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('ui.unsavedChangesTitle')}</AlertDialogTitle>
          <AlertDialogDescription>{t('ui.unsavedChanges')}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          {/* Cancel resets via the auto-close → onOpenChange path above (one
              source of reset, so it isn't called twice). */}
          <AlertDialogCancel>{t('ui.button.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              proceeding.current = true
              blocker.proceed?.()
            }}
          >
            {t('ui.button.leave')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
